import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { SEOHead } from '../components/site/SEOHead';
import { JsonLd } from '../components/site/JsonLd';
import { Lightbox } from '../components/site/Lightbox';
import api, { getFileUrl } from '../lib/api';

const categoryLabels = {
  ongoing: 'Devam Eden',
  completed: 'Tamamlanan',
  future: 'Gelecek'
};

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    api.get(`/projects/${slug}`)
      .then(r => setProject(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Proje bulunamadı</p>
        <Link to="/projeler/devam-eden" className="text-[hsl(var(--gold))] hover:underline">Projelere Dön</Link>
      </div>
    );
  }

  const allImages = [project.cover_image, ...(project.images || [])].filter(Boolean);
  const categorySlug = project.category === 'ongoing' ? 'devam-eden' : project.category === 'completed' ? 'tamamlanan' : 'gelecek';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const projectLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: project.title,
    ...(project.subtitle ? { description: project.subtitle } : {}),
    ...(allImages.length ? { image: allImages.map(getFileUrl) } : {}),
    mainEntityOfPage: `${origin}/proje/${project.slug}`,
  };
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: categoryLabels[project.category] || 'Projeler', item: `${origin}/projeler/${categorySlug}` },
      { '@type': 'ListItem', position: 3, name: project.title, item: `${origin}/proje/${project.slug}` },
    ],
  };

  return (
    <div data-testid="project-detail-page">
      <SEOHead title={`${project.title} - Proje Detayı`} description={project.subtitle} />
      <JsonLd id="project-article" data={projectLd} />
      <JsonLd id="project-breadcrumb" data={breadcrumbLd} />
      
      {/* Hero */}
      <div className="relative h-[50vh] md:h-[60vh] bg-[#0f1419]">
        {allImages.length > 0 && (
          <>
            <img
              src={getFileUrl(allImages[currentImage])}
              alt={project.title}
              className="w-full h-full object-cover cursor-zoom-in"
              onClick={() => setLightboxIndex(currentImage)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/30 to-transparent" />
            
            {allImages.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImage(prev => (prev - 1 + allImages.length) % allImages.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentImage(prev => (prev + 1) % allImages.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </>
        )}
        
        <div className="absolute bottom-0 left-0 right-0 z-10 pb-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <span className="inline-block px-3 py-1 mb-3 text-xs font-medium rounded-full bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]">
              {categoryLabels[project.category]}
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              {project.title}
            </h1>
            {project.subtitle && (
              <p className="text-gray-300 mt-2 text-lg">{project.subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Link
            to={`/projeler/${categorySlug}`}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Projelere Dön
          </Link>
          
          {project.description && (
            <div className="prose max-w-none">
              <p className="text-sm md:text-base leading-relaxed text-muted-foreground whitespace-pre-line">
                {project.description}
              </p>
            </div>
          )}

          {/* Gallery */}
          {allImages.length > 1 && (
            <div className="mt-12">
              <h3 className="font-heading text-xl font-semibold mb-6">Proje Görselleri</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIndex(i)}
                    className="group relative rounded-xl overflow-hidden aspect-[4/3] border-2 border-transparent hover:border-[hsl(var(--gold))] transition-colors duration-200"
                    data-testid={`project-gallery-thumb-${i}`}
                  >
                    <img src={getFileUrl(img)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Lightbox images={allImages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onIndexChange={setLightboxIndex} />
    </div>
  );
}
